import { useMemo, useState, type CSSProperties, type ReactNode } from "react";

import type { ClubView, ClubeDaManchete, ColetivaView, EfeitoView, HeadlineView } from "../game/types";
import { dinheiro, mancheteDoMotor, numero, useT, type Chave } from "../i18n";
import { Crest, strengthColor } from "../ui/atoms";
import { Icone } from "../ui/icons";
import { AvatarDoJogador } from "../ui/avatar/Avatar";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";
import "./jornal.css";

/**
 * Notícias: o jornal do mundo do futebol.
 *
 * Portal no molde do ge: a manchete grande, dois destaques ao lado e o feed
 * com a editoria em cima de cada título. O botão de alcance troca entre o país
 * do seu clube (com os maiores destaques de fora no meio) e o resto do mundo.
 * Na lateral, o que se consulta a cada rodada: em alta, artilharia, seleção da
 * rodada, mercado da temporada e a ponta e o fundo da tabela.
 *
 * Os avisos da carreira, que moravam aqui, foram para o sino do cabeçalho.
 */

type Alcance = "pais" | "mundo";

/** Quantos destaques de fora entram no feed do país, e com que peso mínimo. */
const DE_FORA = { quantos: 2, peso: 60 };

export function Jornal({
  view,
  onOpenPlayer,
  onResponderColetiva,
}: {
  view: ClubView;
  onOpenPlayer: (playerId: string) => void;
  onResponderColetiva: (coletivaId: string, resposta: 0 | 1 | 2 | null) => void;
}): ReactNode {
  const t = useT();
  const [alcance, definirAlcance] = useState<Alcance>("pais");
  const [aberta, definirAberta] = useState<HeadlineView | null>(null);
  const [naColetiva, definirNaColetiva] = useState(false);
  const jornal = view.jornal;

  const capa = useMemo(() => montarCapa(jornal.headlines, alcance), [jornal.headlines, alcance]);
  const emAlta = useMemo(() => maisPesadas(jornal.headlines.filter((h) => h.local), 5), [jornal.headlines]);

  const quando = (h: HeadlineView | undefined): string =>
    h === undefined
      ? ""
      : t("jornal.edicao", {
          temporada: h.season,
          quando: h.round === 0 ? t("jornal.preTemporada") : t("jornal.rodada", { rodada: h.round }),
        });

  return (
    <div className="jornal">
      <section className="jornal-principal">
        <div className="jornal-topo">
          <h2>{t("jornal.titulo")}</h2>
          <div className="segmento" role="group">
            <button type="button" aria-pressed={alcance === "pais"} onClick={() => definirAlcance("pais")}>
              {jornal.countryName}
            </button>
            <button type="button" aria-pressed={alcance === "mundo"} onClick={() => definirAlcance("mundo")}>
              {t("jornal.mundo")}
            </button>
          </div>
          <span className="jornal-edicao num">{quando(capa.manchete)}</span>
        </div>

        {view.coletiva && (
          <div className="jornal-coletiva-barra">
            <Icone nome="Microfone" size={18} />
            <b>{t("coletiva.rotulo")}</b>
            <span className="muted">{view.coletiva.clube}</span>
            <button type="button" className="jornal-coletiva-botao" onClick={() => definirNaColetiva(true)}>
              {t("coletiva.responder")}
            </button>
          </div>
        )}

        {!capa.manchete ? (
          <p className="muted" style={{ margin: "24px 0" }}>
            {t("jornal.vazio")}
          </p>
        ) : (
          <>
            <div className="jornal-herois">
              <Manchete h={capa.manchete} aoAbrir={definirAberta} />
              {capa.destaques.length > 0 && (
                <div className="jornal-destaques">
                  {capa.destaques.map((h) => (
                    <Destaque key={h.id} h={h} aoAbrir={definirAberta} />
                  ))}
                </div>
              )}
            </div>
            <div className="jornal-feed">
              {capa.feed.map((h) => (
                <ItemDoFeed key={h.id} h={h} aoAbrir={definirAberta} quando={quando(h)} />
              ))}
            </div>
          </>
        )}
      </section>

      <aside className="jornal-trilho">
        {emAlta.length > 0 && (
          <div className="jornal-bloco">
            <h5>{t("jornal.emAlta")}</h5>
            <ol className="jornal-alta">
              {emAlta.map((h, i) => (
                <li key={h.id}>
                  <b className="num">{i + 1}</b>
                  <button type="button" onClick={() => definirAberta(h)}>
                    {mancheteDoMotor(h.key, h.params)?.titulo}
                  </button>
                </li>
              ))}
            </ol>
          </div>
        )}

        {view.scorers.length > 0 && (
          <div className="jornal-bloco">
            <h5>
              {t("jornal.artilharia")} · {view.club.leagueName}
            </h5>
            <div className="jornal-lista">
              {view.scorers.slice(0, 5).map((s) => (
                <button type="button" key={s.playerId} className="jornal-linha" onClick={() => onOpenPlayer(s.playerId)}>
                  <Crest colors={s.clubColors} short={s.clubShort} size={18} />
                  <span className="jornal-nome">{s.name}</span>
                  <b className="num">{s.goals}</b>
                </button>
              ))}
            </div>
          </div>
        )}

        {jornal.roundTeam.length > 0 && (
          <div className="jornal-bloco">
            <h5>{t("jornal.selecaoDaRodada")}</h5>
            <SelecaoDaRodada time={jornal.roundTeam} aoAbrir={onOpenPlayer} />
          </div>
        )}

        {jornal.window.length > 0 && (
          <div className="jornal-bloco">
            <h5>{t("jornal.mercado")}</h5>
            <div className="jornal-lista">
              {jornal.window.map((n) => (
                <button type="button" key={n.playerId} className="jornal-linha" onClick={() => onOpenPlayer(n.playerId)}>
                  <span className="jornal-nome">
                    {n.name}
                    <small>
                      {n.from?.name ?? t("jornal.semClube")} → {n.to.name}
                    </small>
                  </span>
                  <b className="num">{dinheiro(n.amount)}</b>
                </button>
              ))}
            </div>
          </div>
        )}

        {view.table.length > 0 && (
          <div className="jornal-bloco">
            <h5>{view.club.leagueName}</h5>
            <MiniTabela view={view} caem={jornal.relegated} />
          </div>
        )}
      </aside>

      {naColetiva && view.coletiva && (
        <Coletiva
          coletiva={view.coletiva}
          aoFechar={() => definirNaColetiva(false)}
          aoResponder={(resposta) => {
            onResponderColetiva((view.coletiva as ColetivaView).id, resposta);
            definirNaColetiva(false);
          }}
        />
      )}

      {aberta && (
        <Materia
          h={aberta}
          quando={quando(aberta)}
          aoFechar={() => definirAberta(null)}
          aoAbrirJogador={(id) => {
            definirAberta(null);
            onOpenPlayer(id);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capa
// ---------------------------------------------------------------------------

function maisPesadas(lista: HeadlineView[], quantas: number): HeadlineView[] {
  const edicao = lista[0];
  if (!edicao) return [];
  return lista
    .filter((h) => h.season === edicao.season && h.round === edicao.round)
    .sort((a, b) => b.weight - a.weight)
    .slice(0, quantas);
}

/**
 * A capa: a manchete, dois destaques e o resto, do mais novo ao mais velho.
 *
 * A manchete e os destaques saem da edição mais recente, pelo peso. No país,
 * as maiores notícias de fora entram no meio do feed, como no jornal de
 * verdade, em que a liga inglesa divide a página com o campeonato daqui.
 */
function montarCapa(
  todas: HeadlineView[],
  alcance: Alcance,
): { manchete: HeadlineView | undefined; destaques: HeadlineView[]; feed: HeadlineView[] } {
  const daqui = todas.filter((h) => (alcance === "pais" ? h.local : !h.local));
  const [manchete, ...destaques] = maisPesadas(daqui, 3);
  const usadas = new Set([manchete, ...destaques].filter(Boolean).map((h) => (h as HeadlineView).id));
  const feed = daqui.filter((h) => !usadas.has(h.id));

  if (alcance === "pais") {
    const recentes = new Set(daqui.slice(0, 1).map((h) => `${h.season}:${h.round}`));
    const deFora = todas
      .filter((h) => !h.local && h.weight >= DE_FORA.peso && (recentes.size === 0 || recentes.has(`${h.season}:${h.round}`)))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, DE_FORA.quantos);
    feed.splice(Math.min(3, feed.length), 0, ...deFora);
  }
  return { manchete, destaques, feed };
}

// ---------------------------------------------------------------------------
// Peças
// ---------------------------------------------------------------------------

/** Fundo claro nas cores do clube, para o rosto multiplicar em cima. */
function tinta(clube: ClubeDaManchete | null, angulo = 135): CSSProperties {
  const [a, b] = clube?.colors ?? ["#2c3f33", "#2c3f33"];
  return {
    background: `linear-gradient(${angulo}deg, color-mix(in srgb, ${a} 42%, white), color-mix(in srgb, ${b} 42%, white))`,
  };
}

function fundo(clube: ClubeDaManchete | null, angulo = 135): CSSProperties {
  const [a, b] = clube?.colors ?? ["#2c3f33", "#2c3f33"];
  return { background: `linear-gradient(${angulo}deg, ${a}, ${b})` };
}

function Chapeu({ h }: { h: HeadlineView }): ReactNode {
  const t = useT();
  return (
    <div className="jornal-chapeu" data-editoria={h.desk}>
      <span>{t(`jornal.editorias.${h.desk}` as Chave)}</span>
      {h.rumor && (
        <>
          <span className="jornal-fonte" title={t(`jornal.fontes.f${h.rumor.fonte}` as Chave)}>
            <span className="jornal-pontos" aria-hidden="true">
              {[1, 2, 3, 4].map((i) => (
                <i key={i} data-on={i <= (h.rumor?.fonte ?? 0) ? "" : undefined} />
              ))}
            </span>
            {t(`jornal.fontes.f${h.rumor.fonte}` as Chave)}
          </span>
          {h.rumor.status !== "aberto" && (
            <span className="jornal-selo" data-status={h.rumor.status}>
              {t(`jornal.selos.${h.rumor.status}` as Chave)}
            </span>
          )}
        </>
      )}
    </div>
  );
}

/** Os efeitos em pílulas: verde o que ajuda, vermelho o que atrapalha. */
function Efeitos({ efeitos }: { efeitos: EfeitoView[] }): ReactNode {
  const t = useT();
  return (
    <div className="jornal-efeitos-chips">
      {efeitos.map((e, i) => {
        const sinal = e.delta > 0 ? `+${e.delta}` : `${e.delta}`;
        // Perder paciência da diretoria é ruim; o resto segue o sinal.
        const bom = e.delta > 0;
        const chave =
          e.kind === "moral" && !e.meu ? "jornal.efeitos.moralOutro" : (`jornal.efeitos.${e.kind}` as Chave);
        return (
          <span key={i} className="jornal-efeito" data-bom={bom ? "" : undefined}>
            {t(chave as Chave, { delta: sinal, jogador: e.nome ?? "", clube: e.nome ?? "" })}
          </span>
        );
      })}
    </div>
  );
}

/** O rosto, o escudo, o placar ou o número grande: a arte da manchete. */
function Arte({ h, tamanho }: { h: HeadlineView; tamanho: "grande" | "media" | "pequena" }): ReactNode {
  const t = useT();
  const art = h.art;
  if (art.kind === "player" || art.kind === "manager") {
    const rostoId = art.kind === "player" ? art.playerId : art.managerId;
    return (
      <div className={`jornal-arte jornal-rosto ${tamanho}`} style={tinta(art.clube)}>
        <AvatarDoJogador playerId={rostoId} nacionalidade={art.nationality} rosto />
        {art.clube && (
          <span className="jornal-arte-escudo">
            <Crest colors={art.clube.colors} short={art.clube.short} size={tamanho === "pequena" ? 22 : 28} />
          </span>
        )}
      </div>
    );
  }
  if (art.kind === "score") {
    return (
      <div className={`jornal-arte jornal-placar ${tamanho}`} style={fundo(art.home, 160)}>
        {art.playerId && tamanho !== "pequena" && (
          <div className="jornal-placar-rosto" style={tinta(art.home)}>
            <AvatarDoJogador playerId={art.playerId} nacionalidade={art.nationality} rosto />
          </div>
        )}
        <div className="jornal-placar-numeros num">
          <Crest colors={art.home.colors} short={art.home.short} size={tamanho === "pequena" ? 22 : 30} />
          <span>{art.homeGoals}</span>
          <i>x</i>
          <span>{art.awayGoals}</span>
          <Crest colors={art.away.colors} short={art.away.short} size={tamanho === "pequena" ? 22 : 30} />
        </div>
      </div>
    );
  }
  if (art.kind === "number") {
    const valor = art.value.startsWith("$") ? dinheiro(Number(art.value.slice(1))) : numero(Number(art.value));
    return (
      <div className={`jornal-arte jornal-numero ${tamanho}`} style={fundo(art.clube, 160)}>
        <span className="num">
          {valor}
          <small>{t(`jornal.arte.${art.label}` as Chave)}</small>
        </span>
      </div>
    );
  }
  return (
    <div className={`jornal-arte jornal-escudo ${tamanho}`} style={fundo(art.clube, 160)}>
      <Crest colors={art.clube.colors} short={art.clube.short} size={tamanho === "pequena" ? 44 : 72} />
    </div>
  );
}

function Manchete({ h, aoAbrir }: { h: HeadlineView; aoAbrir: (h: HeadlineView) => void }): ReactNode {
  const texto = mancheteDoMotor(h.key, h.params);
  return (
    <button type="button" className="jornal-manchete" onClick={() => aoAbrir(h)}>
      <Arte h={h} tamanho="grande" />
      <div className="jornal-manchete-texto">
        <Chapeu h={h} />
        <h3>{texto?.titulo}</h3>
        <p>{texto?.fina}</p>
      </div>
    </button>
  );
}

function Destaque({ h, aoAbrir }: { h: HeadlineView; aoAbrir: (h: HeadlineView) => void }): ReactNode {
  const texto = mancheteDoMotor(h.key, h.params);
  return (
    <button type="button" className="jornal-destaque" onClick={() => aoAbrir(h)}>
      <Arte h={h} tamanho="media" />
      <div>
        <Chapeu h={h} />
        <h4>{texto?.titulo}</h4>
        <p>{texto?.fina}</p>
      </div>
    </button>
  );
}

function ItemDoFeed({ h, aoAbrir, quando }: { h: HeadlineView; aoAbrir: (h: HeadlineView) => void; quando: string }): ReactNode {
  const texto = mancheteDoMotor(h.key, h.params);
  return (
    <button type="button" className="jornal-item" data-meu={h.mine ? "" : undefined} onClick={() => aoAbrir(h)}>
      <Arte h={h} tamanho="pequena" />
      <div>
        <Chapeu h={h} />
        <h4>{texto?.titulo}</h4>
        <p>{texto?.fina}</p>
        <div className="jornal-meta num">{quando}</div>
      </div>
    </button>
  );
}

function Materia({
  h,
  quando,
  aoFechar,
  aoAbrirJogador,
}: {
  h: HeadlineView;
  quando: string;
  aoFechar: () => void;
  aoAbrirJogador: (playerId: string) => void;
}): ReactNode {
  const t = useT();
  useDialogoDeAtalhos({ nome: "Matéria", nivel: 25, aoFechar });
  const texto = mancheteDoMotor(h.key, h.params);
  return (
    <div className="jornal-veu" role="presentation" onClick={aoFechar}>
      <article className="jornal-materia" role="dialog" aria-label={texto?.titulo} onClick={(e) => e.stopPropagation()}>
        <Arte h={h} tamanho="grande" />
        <div className="jornal-materia-corpo">
          <Chapeu h={h} />
          <h2>{texto?.titulo}</h2>
          <p className="jornal-fina">{texto?.fina}</p>
          <div className="jornal-meta num">{quando}</div>
          <p className="jornal-texto">{texto?.corpo}</p>
          {h.effects.some((e) => e.meu) && (
            <div className="jornal-no-seu-clube">
              <h6>{t("jornal.noSeuClube")}</h6>
              <Efeitos efeitos={h.effects.filter((e) => e.meu)} />
            </div>
          )}
          {h.players.length > 0 && (
            <div className="jornal-links">
              {h.players.map((p) => (
                <button type="button" key={p.id} className="ghost" onClick={() => aoAbrirJogador(p.id)}>
                  {t("jornal.verFicha", { jogador: p.name })}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="jornal-materia-pe">
          <button type="button" className="ghost" onClick={aoFechar}>
            {t("comum.fechar")}
          </button>
        </div>
      </article>
    </div>
  );
}

/** Onde cada linha fica no campinho, de cima (ataque) para baixo (gol). */
const ALTURA: Record<string, number> = { ATA: 13, MEI: 38, DEF: 62, GOL: 84 };

function SelecaoDaRodada({
  time,
  aoAbrir,
}: {
  time: ClubView["jornal"]["roundTeam"];
  aoAbrir: (playerId: string) => void;
}): ReactNode {
  return (
    <div className="jornal-campinho">
      {(["ATA", "MEI", "DEF", "GOL"] as const).map((linha) => {
        const daLinha = time.filter((p) => p.line === linha);
        return daLinha.map((p, i) => (
          <button
            type="button"
            key={p.playerId}
            className="jornal-peca"
            style={{ left: `${((i + 1) * 100) / (daLinha.length + 1)}%`, top: `${ALTURA[linha]}%` }}
            onClick={() => aoAbrir(p.playerId)}
            title={`${p.name} · ${p.clube.name}`}
          >
            <b className="num" style={{ background: strengthColor(p.rating * 10) }}>
              {p.rating.toFixed(1)}
            </b>
            <span>{sobrenome(p.name)}</span>
          </button>
        ));
      })}
    </div>
  );
}

function sobrenome(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return partes.length > 1 ? (partes[partes.length - 1] ?? nome) : nome;
}

/** O G4 e a zona de rebaixamento, com o seu clube marcado. */
function MiniTabela({ view, caem }: { view: ClubView; caem: number }): ReactNode {
  const linhas = view.table;
  const topo = linhas.slice(0, 4);
  const fundoDaTabela = caem > 0 ? linhas.slice(-caem) : [];
  const meu = linhas.find((l) => l.isMine);
  const mostrar = [...topo];
  if (meu && !topo.includes(meu) && !fundoDaTabela.includes(meu)) mostrar.push(meu);
  return (
    <table className="jornal-tabela">
      <tbody>
        {mostrar.map((l) => (
          <tr key={l.clubId} data-meu={l.isMine ? "" : undefined}>
            <td className="num">{l.position}</td>
            <td>
              <span className="jornal-linha-tabela">
                <Crest colors={l.colors} short={l.short} size={16} />
                <span className="jornal-nome">{l.name}</span>
              </span>
            </td>
            <td className="num">{l.points}</td>
          </tr>
        ))}
        {fundoDaTabela.length > 0 && (
          <tr className="jornal-salto">
            <td colSpan={3}>···</td>
          </tr>
        )}
        {fundoDaTabela.map((l) => (
          <tr key={l.clubId} data-meu={l.isMine ? "" : undefined} data-queda="">
            <td className="num">{l.position}</td>
            <td>
              <span className="jornal-linha-tabela">
                <Crest colors={l.colors} short={l.short} size={16} />
                <span className="jornal-nome">{l.name}</span>
              </span>
            </td>
            <td className="num">{l.points}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * A coletiva: uma pergunta, três respostas, cada uma com o efeito à vista.
 *
 * Escolher mostra a manchete que vai sair; responder põe a resposta na fila,
 * e ela vale quando a rodada roda. "Sem comentários" fecha sem efeito.
 */
function Coletiva({
  coletiva,
  aoFechar,
  aoResponder,
}: {
  coletiva: ColetivaView;
  aoFechar: () => void;
  aoResponder: (resposta: 0 | 1 | 2 | null) => void;
}): ReactNode {
  const t = useT();
  const [escolhida, definirEscolhida] = useState<0 | 1 | 2 | null>(null);
  useDialogoDeAtalhos({ nome: "Coletiva", nivel: 25, aoFechar });
  const manchete =
    escolhida === null ? null : mancheteDoMotor(coletiva.respostas[escolhida]?.manchete.key ?? "", coletiva.respostas[escolhida]?.manchete.params);
  return (
    <div className="jornal-veu" role="presentation" onClick={aoFechar}>
      <div className="jornal-coletiva" role="dialog" aria-label={t("coletiva.rotulo")} onClick={(e) => e.stopPropagation()}>
        <div className="jornal-coletiva-topo">
          <span className="jornal-chapeu" data-editoria="tecnicos">
            {t("coletiva.rotulo")}
          </span>
          <b>{coletiva.tecnico}</b>
          <span className="muted">{coletiva.clube}</span>
        </div>
        <div className="jornal-pergunta">
          <span className="jornal-mic">
            <Icone nome="Microfone" size={20} />
          </span>
          <div>
            <small className="muted">
              {coletiva.reporter} · {t(`coletiva.veiculos.v${coletiva.veiculo}` as Chave)}
            </small>
            <p>{t(`coletiva.perguntas.${coletiva.tom}` as Chave, { clube: coletiva.clube })}</p>
          </div>
        </div>
        <div className="jornal-respostas">
          {coletiva.respostas.map((r, i) => (
            <button
              type="button"
              key={i}
              className="jornal-resposta"
              aria-pressed={escolhida === i}
              onClick={() => definirEscolhida(i as 0 | 1 | 2)}
            >
              <span>{t(`coletiva.respostas.${coletiva.tom}.r${i}` as Chave)}</span>
              <Efeitos efeitos={r.efeitos} />
            </button>
          ))}
        </div>
        {manchete && (
          <div className="jornal-sai">
            <span className="jornal-chapeu" data-editoria="tecnicos">
              {t("jornal.editorias.tecnicos")}
            </span>
            <b>{manchete.titulo}</b>
          </div>
        )}
        <div className="jornal-coletiva-pe">
          <button type="button" className="ghost" onClick={() => aoResponder(null)}>
            {t("coletiva.semComentarios")}
          </button>
          <button type="button" className="primary" disabled={escolhida === null} onClick={() => aoResponder(escolhida)}>
            {t("coletiva.responder")}
          </button>
        </div>
      </div>
    </div>
  );
}
